
import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface CRMLead {
  id: string;
  source: 'quickscan' | 'webform' | 'referral' | 'import' | 'api' | 'hbe_calculator' | 'truck_calculator' | 'website';
  status: 'new' | 'qualified' | 'unqualified';
  stage: 'nieuw' | 'contact_opgenomen' | 'gekwalificeerd' | 'voorstel' | 'onderhandeling' | 'gewonnen' | 'verloren';
  score: number;
  summary: string | null;
  utm_source: string | null;
  utm_campaign: string | null;
  created_via: string | null;
  owner_id: string | null;
  origin_type: string | null;
  origin_submission_id: string | null;
  next_action_date: string | null;
  meta?: any;
  created_at: string;
  updated_at: string;
  // Related data
  organization?: {
    name: string;
    sector: string | null;
  };
  contact?: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  };
  owner?: {
    name: string | null;
    email: string | null;
  };
  notes?: Array<{
    id: string;
    body: string;
    created_by: string;
    created_at: string;
  }>;
  // Quickscan data if origin_type = 'quickscan'
  quickscan_data?: {
    company_name: string | null;
    email: string | null;
    business_type: string;
    employee_count: string;
    monthly_energy_costs: number;
    energy_potential_score: number | null;
  };
  // HBE Calculator data if origin_type = 'hbe_calculator_submission'
  hbe_data?: {
    company_name: string | null;
    contact_name: string;
    email: string;
    phone: string | null;
    total_kwh: number;
    total_voordeel: number;
    vehicles_count: number;
    wants_proposal: boolean;
    vehicles_data: Array<{
      vehicle_type: string;
      aantal: number;
      km_per_jaar: number;
      kwh_per_km: number;
    }>;
  };
  // Truck Calculator data if origin_type = 'truck_calculator_submission'
  truck_data?: {
    company_name: string | null;
    contact_person: string;
    email: string;
    phone: string | null;
    num_vehicles: number;
    km_per_year: number;
    diesel_cost_per_km: number;
    ev_cost_per_km: number;
    savings_fleet_per_year: number;
    ere_revenue_per_year: number;
    total_benefit: number;
    include_capex: boolean;
    wants_personal_proposal: boolean;
    created_at: string;
    updated_at: string;
  };
}

export const useCRMLeads = () => {
  const [leads, setLeads] = useState<CRMLead[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Track real-time subscriptions
  const channelRef = useRef<any>(null);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      
      // Fetch leads with related data
      const { data: allLeads, error } = await supabase
        .from('leads')
        .select(`
          id,
          source,
          status,
          stage,
          score,
          summary,
          utm_source,
          utm_campaign,
          created_via,
          owner_id,
          origin_type,
          origin_submission_id,
          next_action_date,
          meta,
          created_at,
          updated_at,
          organization:organizations(name, sector),
          contact:contacts(first_name, last_name, email, phone)
        `)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Testleads (bijv. vanaf een preview van de site, migratie 100) horen niet in de lijst.
      // Op meta en niet op de kolom is_test, zodat dit ook werkt voordat 100 is toegepast.
      const leadsData = (allLeads || []).filter(lead => (lead.meta as { is_test?: boolean } | null)?.is_test !== true);

      // Fetch all profiles for owner mapping
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name, email');

      // Create profiles map
      const profilesMap = new Map();
      (profiles || []).forEach(profile => {
        profilesMap.set(profile.id, {
          name: profile.display_name,
          email: profile.email
        });
      });

      // Map database stage values to frontend stage values
      const mapStageToFrontend = (dbStage: string): CRMLead['stage'] => {
        const stageMap: Record<string, CRMLead['stage']> = {
          'new': 'nieuw',
          'contacted': 'contact_opgenomen',
          'qualified': 'gekwalificeerd', 
          'proposal': 'voorstel',
          'negotiation': 'onderhandeling',
          'won': 'gewonnen',
          'lost': 'verloren'
        };
        return stageMap[dbStage] || 'nieuw';
      };

      // Fetch quickscan data for relevant leads
      const quickscanLeadsIds = leadsData?.filter(lead => 
        lead.origin_type === 'quickscan' || lead.origin_type === 'quickscan_submission'
      ).map(lead => lead.origin_submission_id).filter(Boolean) || [];
      
      const intakeLeadsIds = leadsData?.filter(lead => 
        lead.origin_type === 'intake_quickscan'
      ).map(lead => lead.origin_submission_id).filter(Boolean) || [];

      // Fetch quickscan submissions
      const { data: quickscanData } = quickscanLeadsIds.length > 0 
        ? await supabase
            .from('quickscan_submissions')
            .select('id, company_name, email, business_type, employee_count, monthly_energy_costs, energy_potential_score')
            .in('id', quickscanLeadsIds)
        : { data: [] };

      // Fetch intake leads
      const { data: intakeData } = intakeLeadsIds.length > 0
        ? await supabase
            .from('intake_leads')
            .select('scan_id, contact_name, email, branch, company_size, score_10')
            .in('scan_id', intakeLeadsIds)
        : { data: [] };

      // Fetch HBE Calculator leads
      const hbeLeadsIds = leadsData?.filter(lead => 
        lead.origin_type === 'hbe_calculator_submission'
      ).map(lead => lead.origin_submission_id).filter(Boolean) || [];

      const { data: hbeData } = hbeLeadsIds.length > 0
        ? await supabase
            .from('hbe_calculator_submissions')
            .select('id, company_name, contact_name, email, phone, total_kwh, total_voordeel, wants_proposal, vehicles_data')
            .in('id', hbeLeadsIds)
        : { data: [] };

      // Fetch Truck Calculator leads
      const truckLeadsIds = leadsData?.filter(lead => 
        lead.origin_type === 'truck_calculator_submission'
      ).map(lead => lead.origin_submission_id).filter(Boolean) || [];

      const { data: truckData } = truckLeadsIds.length > 0
        ? await supabase
            .from('truck_calculator_submissions')
            .select('id, company_name, contact_person, email, phone, num_vehicles, km_per_year, diesel_cost_per_km, ev_cost_per_km, savings_fleet_per_year, ere_revenue_per_year, total_benefit, include_capex, wants_personal_proposal, created_at, updated_at')
            .in('id', truckLeadsIds)
        : { data: [] };

      // Fetch all notes for these leads from lead_notes table
      const leadIds = leadsData?.map(l => l.id) || [];
      const { data: allNotes } = leadIds.length > 0 
        ? await supabase
            .from('lead_notes')
            .select('id, lead_id, body, created_by, created_at')
            .in('lead_id', leadIds)
            .order('created_at', { ascending: false })
        : { data: [] };

      // Group notes by lead_id
      const notesMap = new Map<string, any[]>();
      (allNotes || []).forEach(note => {
        if (!notesMap.has(note.lead_id)) {
          notesMap.set(note.lead_id, []);
        }
        notesMap.get(note.lead_id)!.push(note);
      });

      // Create quickscan data maps
      const quickscanMap = new Map();
      (quickscanData || []).forEach(qs => {
        quickscanMap.set(qs.id, {
          company_name: qs.company_name,
          email: qs.email,
          business_type: qs.business_type,
          employee_count: qs.employee_count,
          monthly_energy_costs: qs.monthly_energy_costs,
          energy_potential_score: qs.energy_potential_score
        });
      });

      const intakeMap = new Map();
      (intakeData || []).forEach(intake => {
        intakeMap.set(intake.scan_id, {
          company_name: intake.contact_name,
          email: intake.email,
          business_type: intake.branch || 'Onbekend',
          employee_count: intake.company_size || 'Onbekend',
          monthly_energy_costs: 0, // Not available in intake
          energy_potential_score: intake.score_10 ? intake.score_10 * 10 : null
        });
      });

      // Create HBE data map
      const hbeMap = new Map();
      (hbeData || []).forEach(hbe => {
        // Calculate total vehicles count
        const vehiclesCount = hbe.vehicles_data?.reduce((sum: number, v: any) => 
          sum + (v.aantal || 0), 0
        ) || 0;

        hbeMap.set(hbe.id, {
          company_name: hbe.company_name,
          contact_name: hbe.contact_name,
          email: hbe.email,
          phone: hbe.phone,
          total_kwh: hbe.total_kwh,
          total_voordeel: hbe.total_voordeel,
          vehicles_count: vehiclesCount,
          wants_proposal: hbe.wants_proposal,
          vehicles_data: hbe.vehicles_data || []
        });
      });

      // Create Truck Calculator data map
      const truckMap = new Map();
      (truckData || []).forEach(truck => {
        truckMap.set(truck.id, {
          company_name: truck.company_name,
          contact_person: truck.contact_person,
          email: truck.email,
          phone: truck.phone,
          num_vehicles: truck.num_vehicles,
          km_per_year: truck.km_per_year,
          diesel_cost_per_km: truck.diesel_cost_per_km,
          ev_cost_per_km: truck.ev_cost_per_km,
          savings_fleet_per_year: truck.savings_fleet_per_year,
          ere_revenue_per_year: truck.ere_revenue_per_year,
          total_benefit: truck.total_benefit,
          include_capex: truck.include_capex,
          wants_personal_proposal: truck.wants_personal_proposal,
          created_at: truck.created_at,
          updated_at: truck.updated_at
        });
      });

      // Transform data to match our interface
      const transformedLeads = (leadsData || []).map(lead => ({
        ...lead,
        stage: mapStageToFrontend(lead.stage),
        owner: lead.owner_id ? profilesMap.get(lead.owner_id) : undefined,
        notes: notesMap.get(lead.id) || [],
        quickscan_data: lead.origin_submission_id && 
          (lead.origin_type === 'quickscan' || lead.origin_type === 'quickscan_submission' || lead.origin_type === 'intake_quickscan') ? 
          (quickscanMap.get(lead.origin_submission_id) || intakeMap.get(lead.origin_submission_id)) : 
          undefined,
        hbe_data: lead.origin_submission_id && lead.origin_type === 'hbe_calculator_submission' ?
          hbeMap.get(lead.origin_submission_id) :
          undefined,
        truck_data: lead.origin_submission_id && lead.origin_type === 'truck_calculator_submission' ?
          truckMap.get(lead.origin_submission_id) :
          undefined
      }));

      setLeads(transformedLeads as CRMLead[]);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast({
        title: 'Fout bij laden leads',
        description: 'Er ging iets mis bij het ophalen van de leads.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStage = async (leadId: string, newStage: CRMLead['stage']) => {
    try {
      // Map frontend stage values to database stage values
      const mapStageToDatabase = (frontendStage: CRMLead['stage']): string => {
        const stageMap: Record<CRMLead['stage'], string> = {
          'nieuw': 'new',
          'contact_opgenomen': 'contacted',
          'gekwalificeerd': 'qualified',
          'voorstel': 'proposal',
          'onderhandeling': 'negotiation',
          'gewonnen': 'won',
          'verloren': 'lost'
        };
        return stageMap[frontendStage] || 'new';
      };

      const dbStage = mapStageToDatabase(newStage);

      const { error } = await supabase
        .from('leads')
        .update({ stage: dbStage, updated_at: new Date().toISOString() })
        .eq('id', leadId);

      if (error) throw error;

      setLeads(prev => prev.map(lead => 
        lead.id === leadId 
          ? { ...lead, stage: newStage, updated_at: new Date().toISOString() }
          : lead
      ));

      toast({
        title: 'Status bijgewerkt',
        description: 'Lead status is succesvol bijgewerkt.',
      });
    } catch (error) {
      console.error('Error updating lead stage:', error);
      toast({
        title: 'Fout bij bijwerken status',
        description: 'Er ging iets mis bij het bijwerken van de lead status.',
        variant: 'destructive'
      });
    }
  };

  const updateLead = async (leadId: string, updates: Partial<CRMLead>) => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', leadId);

      if (error) throw error;

      // If owner_id is being updated, fetch the owner's profile info
      let ownerInfo = undefined;
      if (updates.owner_id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name, email')
          .eq('id', updates.owner_id)
          .single();
        
        if (profile) {
          ownerInfo = {
            name: profile.display_name,
            email: profile.email
          };
        }
      }

      setLeads(prev => prev.map(lead => 
        lead.id === leadId 
          ? { 
              ...lead, 
              ...updates, 
              updated_at: new Date().toISOString(),
              ...(ownerInfo && { owner: ownerInfo })
            }
          : lead
      ));

      toast({
        title: 'Lead bijgewerkt',
        description: 'Lead details zijn succesvol bijgewerkt.',
      });
    } catch (error) {
      console.error('Error updating lead:', error);
      toast({
        title: 'Fout bij bijwerken lead',
        description: 'Er ging iets mis bij het bijwerken van de lead.',
        variant: 'destructive'
      });
    }
  };

  const addNote = async (leadId: string, body: string) => {
    try {
      const currentUser = await supabase.auth.getUser();
      
      // Insert note into database
      const { data, error } = await supabase
        .from('lead_notes')
        .insert({
          lead_id: leadId,
          body,
          created_by: currentUser.data.user?.id
        })
        .select('id, lead_id, body, created_by, created_at')
        .single();

      if (error) throw error;

      // Local state is updated via realtime subscription
      toast({
        title: 'Notitie toegevoegd',
        description: 'De notitie is succesvol toegevoegd.',
      });
    } catch (error) {
      console.error('Error adding note:', error);
      toast({
        title: 'Fout bij toevoegen notitie',
        description: 'Er ging iets mis bij het toevoegen van de notitie.',
        variant: 'destructive'
      });
    }
  };

  const updateNote = async (noteId: string, body: string) => {
    try {
      const currentUser = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('lead_notes')
        .update({ body })
        .eq('id', noteId)
        .eq('created_by', currentUser.data.user?.id); // Only own notes
        
      if (error) throw error;

      toast({
        title: 'Notitie bijgewerkt',
        description: 'De notitie is succesvol bijgewerkt.',
      });
    } catch (error) {
      console.error('Error updating note:', error);
      toast({
        title: 'Fout bij bijwerken notitie',
        description: 'Er ging iets mis bij het bijwerken van de notitie.',
        variant: 'destructive'
      });
    }
  };

  const deleteNote = async (noteId: string) => {
    try {
      const currentUser = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('lead_notes')
        .delete()
        .eq('id', noteId)
        .eq('created_by', currentUser.data.user?.id); // Only own notes
        
      if (error) throw error;

      // Optimistic update - remove note from UI immediately
      setLeads(prev => prev.map(lead => ({
        ...lead,
        notes: (lead.notes || []).filter(n => n.id !== noteId)
      })));

      toast({
        title: 'Notitie verwijderd',
        description: 'De notitie is succesvol verwijderd.',
      });
    } catch (error) {
      console.error('Error deleting note:', error);
      toast({
        title: 'Fout bij verwijderen notitie',
        description: 'Er ging iets mis bij het verwijderen van de notitie.',
        variant: 'destructive'
      });
    }
  };

  const deleteLead = async (leadId: string) => {
    try {
      // Optimistic update - remove from UI immediately
      setLeads(prev => prev.filter(lead => lead.id !== leadId));

      const { error } = await supabase
        .from('leads')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', leadId);

      if (error) throw error;

      toast({
        title: 'Lead verwijderd',
        description: 'De lead is succesvol verwijderd.',
      });

      return true; // Signal successful deletion
    } catch (error) {
      console.error('Error deleting lead:', error);
      // Revert optimistic update on error
      fetchLeads();
      toast({
        title: 'Fout bij verwijderen lead',
        description: 'Er ging iets mis bij het verwijderen van de lead.',
        variant: 'destructive'
      });
      return false;
    }
  };

  const createLead = async (leadData: Partial<CRMLead>) => {
    try {
      const currentUser = await supabase.auth.getUser();
      
      // Map frontend stage to database stage
      const mapStageToDatabase = (frontendStage: CRMLead['stage']): string => {
        const stageMap: Record<CRMLead['stage'], string> = {
          'nieuw': 'new',
          'contact_opgenomen': 'contacted',
          'gekwalificeerd': 'qualified',
          'voorstel': 'proposal',
          'onderhandeling': 'negotiation',
          'gewonnen': 'won',
          'verloren': 'lost'
        };
        return stageMap[frontendStage] || 'new';
      };
      
      // Clean the data to match database columns
      const insertData = {
        source: leadData.source || 'webform',
        status: leadData.status || 'new',
        stage: mapStageToDatabase(leadData.stage || 'nieuw'),
        score: leadData.score || 0,
        summary: leadData.summary,
        utm_source: leadData.utm_source,
        utm_campaign: leadData.utm_campaign,
        created_via: leadData.created_via,
        owner_id: leadData.owner_id || currentUser.data.user?.id || '',
        origin_type: leadData.origin_type,
        origin_submission_id: leadData.origin_submission_id,
        next_action_date: leadData.next_action_date
      };

      const { data, error } = await supabase
        .from('leads')
        .insert(insertData)
        .select(`
          id,
          source,
          status,
          stage,
          score,
          summary,
          utm_source,
          utm_campaign,
          created_via,
          owner_id,
          origin_type,
          origin_submission_id,
          next_action_date,
          created_at,
          updated_at
        `)
        .single();

      if (error) throw error;

      // Create lead object with proper typing
      const newLead: CRMLead = {
        ...data,
        source: data.source as CRMLead['source'],
        status: data.status as CRMLead['status'],
        stage: data.stage as CRMLead['stage'],
        organization: leadData.organization,
        contact: leadData.contact,
        owner: leadData.owner,
        notes: [],
        quickscan_data: leadData.quickscan_data
      };

      setLeads(prev => [newLead, ...prev]);

      toast({
        title: 'Lead aangemaakt',
        description: 'De nieuwe lead is succesvol aangemaakt.',
      });

      return newLead;
    } catch (error) {
      console.error('Error creating lead:', error);
      toast({
        title: 'Fout bij aanmaken lead',
        description: 'Er ging iets mis bij het aanmaken van de lead.',
        variant: 'destructive'
      });
      throw error;
    }
  };

  // Bulk operations
  const bulkUpdateStage = async (leadIds: string[], newStage: CRMLead['stage']) => {
    try {
      // Map frontend stage to database stage
      const mapStageToDatabase = (frontendStage: CRMLead['stage']): string => {
        const stageMap: Record<CRMLead['stage'], string> = {
          'nieuw': 'new',
          'contact_opgenomen': 'contacted',
          'gekwalificeerd': 'qualified',
          'voorstel': 'proposal',
          'onderhandeling': 'negotiation',
          'gewonnen': 'won',
          'verloren': 'lost'
        };
        return stageMap[frontendStage] || 'new';
      };

      const dbStage = mapStageToDatabase(newStage);

      const { error } = await supabase
        .from('leads')
        .update({ stage: dbStage, updated_at: new Date().toISOString() })
        .in('id', leadIds);

      if (error) throw error;

      // Update local state
      setLeads(prev => prev.map(lead => 
        leadIds.includes(lead.id) 
          ? { ...lead, stage: newStage, updated_at: new Date().toISOString() }
          : lead
      ));

      toast({
        title: 'Bulk update voltooid',
        description: `${leadIds.length} lead${leadIds.length > 1 ? 's' : ''} bijgewerkt naar ${newStage}.`,
      });
    } catch (error) {
      console.error('Error bulk updating stage:', error);
      toast({
        title: 'Fout bij bulk update',
        description: 'Er ging iets mis bij het bijwerken van de leads.',
        variant: 'destructive'
      });
    }
  };

  const bulkUpdateOwner = async (leadIds: string[], ownerId: string) => {
    try {
      const { error } = await supabase
        .from('leads')
        .update({ owner_id: ownerId, updated_at: new Date().toISOString() })
        .in('id', leadIds);

      if (error) throw error;

      // Fetch owner profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name, email')
        .eq('id', ownerId)
        .single();

      const ownerInfo = profile ? {
        name: profile.display_name,
        email: profile.email
      } : undefined;

      // Update local state
      setLeads(prev => prev.map(lead => 
        leadIds.includes(lead.id) 
          ? { 
              ...lead, 
              owner_id: ownerId, 
              owner: ownerInfo,
              updated_at: new Date().toISOString() 
            }
          : lead
      ));

      toast({
        title: 'Eigenaar toegewezen',
        description: `${leadIds.length} lead${leadIds.length > 1 ? 's' : ''} toegewezen aan ${ownerInfo?.name || ownerInfo?.email}.`,
      });
    } catch (error) {
      console.error('Error bulk updating owner:', error);
      toast({
        title: 'Fout bij toewijzen eigenaar',
        description: 'Er ging iets mis bij het toewijzen van de eigenaar.',
        variant: 'destructive'
      });
    }
  };

  const bulkDelete = async (leadIds: string[]): Promise<boolean> => {
    try {
      // Optimistic update - remove from UI immediately
      setLeads(prev => prev.filter(lead => !leadIds.includes(lead.id)));

      const { error } = await supabase
        .from('leads')
        .update({ deleted_at: new Date().toISOString() })
        .in('id', leadIds);

      if (error) throw error;

      toast({
        title: 'Leads verwijderd',
        description: `${leadIds.length} lead${leadIds.length > 1 ? 's' : ''} succesvol verwijderd.`,
      });

      return true;
    } catch (error) {
      console.error('Error bulk deleting leads:', error);
      // Revert optimistic update on error
      fetchLeads();
      toast({
        title: 'Fout bij verwijderen leads',
        description: 'Er ging iets mis bij het verwijderen van de leads.',
        variant: 'destructive'
      });
      return false;
    }
  };

  // Track notes channel ref
  const notesChannelRef = useRef<any>(null);

  useEffect(() => {
    fetchLeads();
    
    // Set up real-time subscription for leads
    const setupRealtimeSubscription = () => {
      channelRef.current = supabase
        .channel('leads-changes')
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'leads'
        }, (payload) => {
          console.log('New lead inserted:', payload);
          handleRealtimeInsert(payload.new);
        })
        .on('postgres_changes', {
          event: 'UPDATE', 
          schema: 'public',
          table: 'leads'
        }, (payload) => {
          console.log('Lead updated:', payload);
          handleRealtimeUpdate(payload.new);
        })
        .on('postgres_changes', {
          event: 'DELETE',
          schema: 'public', 
          table: 'leads'
        }, (payload) => {
          console.log('Lead deleted:', payload);
          handleRealtimeDelete(payload.old.id);
        })
        .subscribe();
        
      // Set up real-time subscription for lead_notes
      notesChannelRef.current = supabase
        .channel('lead-notes-changes')
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'lead_notes'
        }, (payload) => {
          console.log('New lead note inserted:', payload);
          handleNoteInsert(payload.new);
        })
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'lead_notes'
        }, (payload) => {
          console.log('Lead note updated:', payload);
          handleNoteUpdate(payload.new);
        })
        .on('postgres_changes', {
          event: 'DELETE',
          schema: 'public',
          table: 'lead_notes'
        }, (payload) => {
          console.log('Lead note deleted:', payload);
          handleNoteDelete(payload.old);
        })
        .subscribe();
    };

    setupRealtimeSubscription();

    // Cleanup subscription on unmount
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      if (notesChannelRef.current) {
        supabase.removeChannel(notesChannelRef.current);
        notesChannelRef.current = null;
      }
    };
  }, []);

  const handleNoteInsert = (newNote: any) => {
    setLeads(prev => prev.map(lead => 
      lead.id === newNote.lead_id 
        ? { 
            ...lead, 
            notes: [newNote, ...(lead.notes || []).filter(n => n.id !== newNote.id)]
          }
        : lead
    ));
  };

  const handleNoteUpdate = (updatedNote: any) => {
    setLeads(prev => prev.map(lead => 
      lead.id === updatedNote.lead_id 
        ? { 
            ...lead, 
            notes: (lead.notes || []).map(n => n.id === updatedNote.id ? updatedNote : n)
          }
        : lead
    ));
  };

  const handleNoteDelete = (deletedNote: any) => {
    setLeads(prev => prev.map(lead => 
      lead.id === deletedNote.lead_id 
        ? { 
            ...lead, 
            notes: (lead.notes || []).filter(n => n.id !== deletedNote.id)
          }
        : lead
    ));
  };

  const handleRealtimeInsert = async (newLead: any) => {
    if (newLead?.meta?.is_test === true) return; // testlead, zie fetchLeads
    try {
      // Fetch complete lead data with relations
      const { data } = await supabase
        .from('leads')
        .select(`
          id,
          source,
          status,
          stage,
          score,
          summary,
          utm_source,
          utm_campaign,
          created_via,
          owner_id,
          origin_type,
          origin_submission_id,
          next_action_date,
          created_at,
          updated_at,
          organization:organizations(name, sector),
          contact:contacts(first_name, last_name, email, phone)
        `)
        .eq('id', newLead.id)
        .single();

      if (data) {
        // Transform and add to leads
        const transformedLead = await transformSingleLead(data);
        setLeads(prev => [transformedLead, ...prev]);
        
        toast({
          title: 'Nieuwe lead',
          description: `Er is een nieuwe lead toegevoegd: ${transformedLead.organization?.name || transformedLead.quickscan_data?.company_name || 'Onbekend'}`,
        });
      }
    } catch (error) {
      console.error('Error handling realtime insert:', error);
    }
  };

  const handleRealtimeUpdate = async (updatedLead: any) => {
    try {
      // Skip if this is our own optimistic update (prevent duplicate updates)
      const currentLead = leads.find(l => l.id === updatedLead.id);
      if (currentLead && new Date(currentLead.updated_at) >= new Date(updatedLead.updated_at)) {
        return;
      }

      // Fetch complete updated lead data
      const { data } = await supabase
        .from('leads')
        .select(`
          id,
          source,
          status,
          stage,
          score,
          summary,
          utm_source,
          utm_campaign,
          created_via,
          owner_id,
          origin_type,
          origin_submission_id,
          next_action_date,
          created_at,
          updated_at,
          organization:organizations(name, sector),
          contact:contacts(first_name, last_name, email, phone)
        `)
        .eq('id', updatedLead.id)
        .single();

      if (data) {
        const transformedLead = await transformSingleLead(data);
        setLeads(prev => prev.map(lead => 
          lead.id === updatedLead.id ? transformedLead : lead
        ));
      }
    } catch (error) {
      console.error('Error handling realtime update:', error);
    }
  };

  const handleRealtimeDelete = (deletedLeadId: string) => {
    setLeads(prev => prev.filter(lead => lead.id !== deletedLeadId));
    
    toast({
      title: 'Lead verwijderd',
      description: 'Een lead is verwijderd door een andere gebruiker.',
    });
  };

  const transformSingleLead = async (leadData: any): Promise<CRMLead> => {
    // Get owner profile
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, display_name, email')
      .eq('id', leadData.owner_id || '')
      .single();

    // Map stage and get quickscan data
    const mapStageToFrontend = (dbStage: string): CRMLead['stage'] => {
      const stageMap: Record<string, CRMLead['stage']> = {
        'new': 'nieuw',
        'contacted': 'contact_opgenomen',
        'qualified': 'gekwalificeerd',
        'proposal': 'voorstel',
        'negotiation': 'onderhandeling',
        'won': 'gewonnen',
        'lost': 'verloren'
      };
      return stageMap[dbStage] || 'nieuw';
    };

    // Get quickscan data if needed
    let quickscanData = undefined;
    if (leadData.origin_submission_id && (leadData.origin_type === 'quickscan' || leadData.origin_type === 'quickscan_submission')) {
      const { data: qs } = await supabase
        .from('quickscan_submissions')
        .select('id, company_name, email, business_type, employee_count, monthly_energy_costs, energy_potential_score')
        .eq('id', leadData.origin_submission_id)
        .single();
      
      if (qs) {
        quickscanData = {
          company_name: qs.company_name,
          email: qs.email,
          business_type: qs.business_type,
          employee_count: qs.employee_count,
          monthly_energy_costs: qs.monthly_energy_costs,
          energy_potential_score: qs.energy_potential_score
        };
      }
    } else if (leadData.origin_submission_id && leadData.origin_type === 'intake_quickscan') {
      const { data: intake } = await supabase
        .from('intake_leads')
        .select('scan_id, contact_name, email, branch, company_size, score_10')
        .eq('scan_id', leadData.origin_submission_id)
        .single();
      
      if (intake) {
        quickscanData = {
          company_name: intake.contact_name,
          email: intake.email,
          business_type: intake.branch || 'Onbekend',
          employee_count: intake.company_size || 'Onbekend',
          monthly_energy_costs: 0,
          energy_potential_score: intake.score_10 ? intake.score_10 * 10 : null
        };
      }
    }

    return {
      ...leadData,
      stage: mapStageToFrontend(leadData.stage),
      owner: profiles ? { name: profiles.display_name, email: profiles.email } : undefined,
      notes: [],
      quickscan_data: quickscanData
    };
  };

  return {
    leads,
    loading,
    refetch: fetchLeads,
    updateLeadStage,
    updateLead,
    addNote,
    updateNote,
    deleteNote,
    deleteLead,
    createLead,
    bulkUpdateStage,
    bulkUpdateOwner,
    bulkDelete
  };
};
